"use client";

/**
 * Simulateur de VALEUR adapté à un service VTC / chauffeur privé (≠ ROI commission).
 *
 * Angle « gain de temps » : quand on est conduit plutôt qu'au volant, le temps de
 * trajet redevient productif. L'outil chiffre ce temps récupéré à partir
 * d'hypothèses SAISIES PAR L'UTILISATEUR (jamais de chiffres inventés), et estampille
 * la sortie « estimation, pas une garantie » — conforme à la méthode (levier de
 * conversion §5) ET aux règles anti-spam (aucune donnée fabriquée).
 *
 * Auto-suffisant : state local, calcul pur (SSR-safe, hydratation déterministe),
 * i18n FR/EN/AR via tr(), classes logiques (RTL-safe).
 */
import { useState } from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { tr, type I18n } from "@/config/localeText";

type SimDict = {
  eyebrow: string;
  heading: string;
  intro: string;
  tripsLabel: string;
  durationLabel: string;
  hourValueLabel: string;
  hourValuePlaceholder: string;
  resultEyebrow: string;
  perMonth: string;
  perYear: string;
  daysEquiv: string;
  valueYear: string;
  hoursUnit: string;
  disclaimer: string;
  cta: string;
};

const SIM_UI: I18n<SimDict> = {
  fr: {
    eyebrow: "Estimez votre gain",
    heading: "Combien de temps un chauffeur vous fait-il gagner ?",
    intro:
      "Conduit plutôt qu'au volant, votre temps de trajet redevient productif (appels, e-mails, repos) — sans parking ni fatigue. Ajustez vos hypothèses :",
    tripsLabel: "Trajets par mois",
    durationLabel: "Durée moyenne d'un trajet (min)",
    hourValueLabel: "Valeur de votre heure (€, optionnel)",
    hourValuePlaceholder: "ex. 80",
    resultEyebrow: "Votre estimation",
    perMonth: "récupérées par mois",
    perYear: "par an",
    daysEquiv: "journées de travail (8 h) par an",
    valueYear: "de temps productif récupéré par an",
    hoursUnit: "h",
    disclaimer:
      "Estimation indicative basée sur vos hypothèses — ce n'est pas une garantie.",
    cta: "Réserver mon chauffeur",
  },
  en: {
    eyebrow: "Estimate your gain",
    heading: "How much time does a private chauffeur save you?",
    intro:
      "Driven instead of driving, your travel time becomes productive again (calls, emails, rest) — no parking, no fatigue. Adjust your assumptions:",
    tripsLabel: "Trips per month",
    durationLabel: "Average trip duration (min)",
    hourValueLabel: "Value of your hour (€, optional)",
    hourValuePlaceholder: "e.g. 80",
    resultEyebrow: "Your estimate",
    perMonth: "reclaimed per month",
    perYear: "per year",
    daysEquiv: "working days (8 h) per year",
    valueYear: "of productive time reclaimed per year",
    hoursUnit: "h",
    disclaimer:
      "Indicative estimate based on your assumptions — not a guarantee.",
    cta: "Book my chauffeur",
  },
  ar: {
    eyebrow: "احسب ما توفّره",
    heading: "كم من الوقت يوفّر لك السائق الخاص؟",
    intro:
      "حين تكون مُساقًا لا خلف المقود، يصبح وقت تنقّلك منتِجًا من جديد (مكالمات، بريد إلكتروني، راحة) — دون بحث عن موقف ودون إرهاق. عدّل افتراضاتك:",
    tripsLabel: "عدد الرحلات شهريًا",
    durationLabel: "متوسط مدة الرحلة (دقيقة)",
    hourValueLabel: "قيمة ساعتك (يورو، اختياري)",
    hourValuePlaceholder: "مثال: 80",
    resultEyebrow: "تقديرك",
    perMonth: "مستردّة شهريًا",
    perYear: "سنويًا",
    daysEquiv: "أيام عمل (8 ساعات) سنويًا",
    valueYear: "من الوقت المنتِج المستردّ سنويًا",
    hoursUnit: "ساعة",
    disclaimer: "تقدير إرشادي مبني على افتراضاتك — وليس ضمانًا.",
    cta: "احجز سائقي الخاص",
  },
};

/** Parse robuste : virgule décimale tolérée, négatifs/NaN → 0. */
const toNum = (raw: string): number => {
  const v = parseFloat(raw.replace(",", "."));
  return Number.isFinite(v) && v > 0 ? v : 0;
};

/** Arrondi entier + séparateur de milliers par espace (lisible FR/EN/AR). */
const fmt = (n: number): string =>
  Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");

/** Une décimale, sans zéro inutile. */
const fmt1 = (n: number): string =>
  (Math.round(n * 10) / 10).toString().replace(".", ",");

type Props = {
  locale?: Locale;
  /** Cible du CTA (ancre formulaire embarqué…). Défaut : page réservation. */
  ctaHref?: string;
};

export function TimeValueSimulator({
  locale = "fr",
  ctaHref,
}: Props): JSX.Element {
  const ui = tr(SIM_UI, locale);
  const [trips, setTrips] = useState("8");
  const [duration, setDuration] = useState("45");
  const [hourValue, setHourValue] = useState("");

  const hoursMonth = (toNum(trips) * toNum(duration)) / 60;
  const hoursYear = hoursMonth * 12;
  const daysYear = hoursYear / 8;
  const hv = toNum(hourValue);
  const valueYear = hv > 0 ? hoursYear * hv : null;

  const href = ctaHref ?? localeNavHref("/reservation", locale);

  const inputCls =
    "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm font-semibold text-black outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/30";
  const labelCls =
    "block text-xs font-medium uppercase tracking-[0.12em] text-black/55";

  return (
    <section className="bg-white py-14 text-black">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <p className="mb-2 text-xs uppercase tracking-[0.18em] text-gold-800">
          {ui.eyebrow}
        </p>
        <h2 className="mb-3 font-space-age text-xl tracking-[0.2em]">
          {ui.heading}
        </h2>
        <p className="mb-6 max-w-3xl text-sm text-black/70">{ui.intro}</p>

        <div className="grid gap-5 rounded-2xl border border-black/10 bg-black/[0.02] p-5 md:grid-cols-[0.9fr_1.1fr] md:p-7">
          {/* Hypothèses saisies par l'utilisateur */}
          <div className="grid content-start gap-4">
            <div>
              <label htmlFor="sim-trips" className={labelCls}>
                {ui.tripsLabel}
              </label>
              <input
                id="sim-trips"
                type="number"
                inputMode="numeric"
                min={0}
                value={trips}
                onChange={(e) => setTrips(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="sim-duration" className={labelCls}>
                {ui.durationLabel}
              </label>
              <input
                id="sim-duration"
                type="number"
                inputMode="numeric"
                min={0}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="sim-hourvalue" className={labelCls}>
                {ui.hourValueLabel}
              </label>
              <input
                id="sim-hourvalue"
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={ui.hourValuePlaceholder}
                value={hourValue}
                onChange={(e) => setHourValue(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Résultats calculés */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/30 bg-primary/[0.06] p-5">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-gold-800">
                {ui.resultEyebrow}
              </p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-space-age text-4xl leading-none text-black">
                  {fmt1(hoursMonth)} {ui.hoursUnit}
                </span>
                <span className="text-sm text-black/70">{ui.perMonth}</span>
              </div>
              <div className="mt-4 grid gap-2 text-sm text-black/80">
                <p>
                  <span className="font-semibold text-black">
                    ≈ {fmt(hoursYear)} {ui.hoursUnit}
                  </span>{" "}
                  {ui.perYear}
                </p>
                <p>
                  <span className="font-semibold text-black">
                    ≈ {fmt1(daysYear)}
                  </span>{" "}
                  {ui.daysEquiv}
                </p>
                {valueYear != null && (
                  <p>
                    <span className="font-semibold text-black">
                      ≈ {fmt(valueYear)} €
                    </span>{" "}
                    {ui.valueYear}
                  </p>
                )}
              </div>
            </div>
            <Link
              href={href}
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white"
            >
              <Icon icon="mdi:calendar-check" className="h-4 w-4" />
              {ui.cta}
            </Link>
          </div>
        </div>

        <p className="mt-3 text-xs text-black/50">{ui.disclaimer}</p>
      </div>
    </section>
  );
}

export default TimeValueSimulator;
